import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VacationRequest, VacationRequestStatus } from '../../types';
import { VacationSummaryCard } from './VacationSummaryCard';

interface VacationApprovalSummaryProps {
  requests: VacationRequest[];
  loading?: boolean;
}

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const parseDay = (value: string): Date => startOfDay(new Date(value));

/**
 * Manager/admin approval overview: four summary tiles derived from the team's
 * requests (pending review, approved this month, on leave today, upcoming in
 * the next 30 days). Tiles reuse the shared {@link VacationSummaryCard}.
 */
export const VacationApprovalSummary: React.FC<VacationApprovalSummaryProps> = ({
  requests,
  loading,
}) => {
  const { t } = useTranslation('vacations');

  const stats = useMemo(() => {
    const today = startOfDay(new Date());
    const in30Days = new Date(today);
    in30Days.setDate(in30Days.getDate() + 30);

    let pendingReview = 0;
    let approvedThisMonth = 0;
    let onLeaveToday = 0;
    let upcoming = 0;

    requests.forEach((req) => {
      if (req.status === VacationRequestStatus.PENDING) {
        pendingReview += 1;
      }
      if (req.status === VacationRequestStatus.APPROVED) {
        const start = parseDay(req.startDate);
        const end = parseDay(req.endDate);

        const reviewedRef = req.reviewedAt ?? req.updatedAt;
        if (reviewedRef) {
          const reviewed = parseDay(reviewedRef);
          if (
            reviewed.getFullYear() === today.getFullYear() &&
            reviewed.getMonth() === today.getMonth()
          ) {
            approvedThisMonth += 1;
          }
        }

        if (start <= today && today <= end) {
          onLeaveToday += 1;
        }
        if (start > today && start <= in30Days) {
          upcoming += 1;
        }
      }
    });

    return { pendingReview, approvedThisMonth, onLeaveToday, upcoming };
  }, [requests]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="vacation-approval-summary-loading">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="vacation-approval-summary">
      <VacationSummaryCard
        label={t('approvals.stats.pendingReview')}
        value={stats.pendingReview}
        valueClassName="text-orange-500"
        testId="vacation-approval-pending"
      />
      <VacationSummaryCard
        label={t('approvals.stats.approvedThisMonth')}
        value={stats.approvedThisMonth}
        testId="vacation-approval-approved"
      />
      <VacationSummaryCard
        label={t('approvals.stats.onLeaveToday')}
        value={stats.onLeaveToday}
        testId="vacation-approval-onleave"
      />
      <VacationSummaryCard
        label={t('approvals.stats.upcoming')}
        value={stats.upcoming}
        testId="vacation-approval-upcoming"
      />
    </div>
  );
};
